// Per-project cycle management section — rendered inside ProjectSettingsPanel.
// Features: list (name, status badge, date range), add, inline-edit (name/dates/status),
// delete with confirm. Mirrors StatusesSection structure and Tailwind conventions.

import { useState } from "preact/hooks";
import { Plus, Trash2, ChevronDown, ChevronRight } from "lucide-preact";
import type { ApiCycle, CycleStatus } from "@juuplan/shared";
import { CYCLE_STATUSES } from "@juuplan/shared";
import { toastSuccess } from "../lib/toast";
import {
  useCycles,
  addCycle,
  patchCycle,
  removeCycle,
} from "../store/cycles";
import { CycleBurndown } from "./CycleBurndown";

import { Button } from "./ui/Button";

const STATUS_LABEL: Record<CycleStatus, string> = {
  upcoming: "Geplant",
  active: "Aktiv",
  completed: "Abgeschlossen",
};

interface Props {
  projectId: number;
  /** When false the mutating controls are hidden (view-only role). */
  canEdit: boolean;
}

interface EditState {
  name: string;
  status: CycleStatus;
  start_date: string;
  end_date: string;
}

/**
 * Per-project cycle management list.
 * Renders inside ProjectSettingsPanel as an accordion-style section.
 */
export function CyclesSection(props: Props): preact.JSX.Element {
  const { projectId, canEdit } = props;
  const { items, loading, error } = useCycles(projectId);

  // Row currently being edited (id -> draft)
  const [editing, setEditing] = useState<Map<number, EditState>>(new Map());
  // Rows pending a save
  const [saving, setSaving] = useState<Set<number>>(new Set());
  // Rows pending a delete confirm
  const [confirmDel, setConfirmDel] = useState<Set<number>>(new Set());

  // Rows with burndown expanded
  const [expandedBurndown, setExpandedBurndown] = useState<Set<number>>(
    new Set()
  );

  // New-cycle form state
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newStatus, setNewStatus] = useState<CycleStatus>("upcoming");
  const [newStartDate, setNewStartDate] = useState("");
  const [newEndDate, setNewEndDate] = useState("");
  const [creating, setCreating] = useState(false);

  // ---- helpers ----

  const startEdit = (c: ApiCycle): void => {
    setEditing((prev) => {
      const next = new Map(prev);
      next.set(c.id, {
        name: c.name,
        status: c.status,
        start_date: c.start_date ?? "",
        end_date: c.end_date ?? "",
      });
      return next;
    });
  };

  const cancelEdit = (id: number): void => {
    setEditing((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  };

  const setDraft = (id: number, patch: Partial<EditState>): void => {
    setEditing((prev) => {
      const cur = prev.get(id);
      if (!cur) return prev;
      const next = new Map(prev);
      next.set(id, { ...cur, ...patch });
      return next;
    });
  };

  const commitEdit = async (c: ApiCycle): Promise<void> => {
    const draft = editing.get(c.id);
    if (!draft) return;
    setSaving((prev) => new Set(prev).add(c.id));
    try {
      await patchCycle(c.id, projectId, {
        name: draft.name.trim() || c.name,
        status: draft.status,
        start_date: draft.start_date || null,
        end_date: draft.end_date || null,
      });
      cancelEdit(c.id);
      toastSuccess("Zyklus gespeichert.");
    } catch {
      // api wrapper already toasted
    } finally {
      setSaving((prev) => {
        const next = new Set(prev);
        next.delete(c.id);
        return next;
      });
    }
  };

  const handleDelete = async (c: ApiCycle): Promise<void> => {
    setSaving((prev) => new Set(prev).add(c.id));
    try {
      await removeCycle(c.id, projectId);
      setConfirmDel((prev) => {
        const next = new Set(prev);
        next.delete(c.id);
        return next;
      });
      toastSuccess("Zyklus gelöscht.");
    } catch {
      // api wrapper already toasted
    } finally {
      setSaving((prev) => {
        const next = new Set(prev);
        next.delete(c.id);
        return next;
      });
    }
  };

  const submitNew = async (): Promise<void> => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      await addCycle(projectId, {
        name,
        status: newStatus,
        start_date: newStartDate || null,
        end_date: newEndDate || null,
      });
      setNewName("");
      setNewStatus("upcoming");
      setNewStartDate("");
      setNewEndDate("");
      setShowNew(false);
      toastSuccess("Zyklus angelegt.");
    } catch {
      // api wrapper already toasted
    } finally {
      setCreating(false);
    }
  };

  const formatDateRange = (start: string | null, end: string | null): string => {
    if (!start && !end) return "";
    if (start && end) return `${start} – ${end}`;
    if (start) return `ab ${start}`;
    return `bis ${end}`;
  };

  // ---- render ----

  return (
    <div class="flex flex-col gap-2">
      <div class="text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
        Zyklen
      </div>

      {loading && (
        <p class="text-xs text-[color:var(--color-on-surface-variant)]">Lädt…</p>
      )}

      {error && (
        <p class="text-xs text-[color:var(--color-error)]">
          Fehler: {error}
        </p>
      )}

      {!loading && !error && items.length === 0 && (
        <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
          Noch keine Zyklen.
        </p>
      )}

      {!loading && items.map((c) => {
        const draft = editing.get(c.id);
        const isSaving = saving.has(c.id);
        const isConfirmDel = confirmDel.has(c.id);
        const dateRange = formatDateRange(c.start_date, c.end_date);

        return (
          <div
            key={c.id}
            class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] overflow-hidden"
          >
            {/* Row header */}
            <div class="flex items-center gap-2 px-2 py-1.5">
              {/* Name */}
              <span class="flex-1 text-xs font-medium text-[color:var(--color-on-surface)] truncate">
                {draft ? draft.name || c.name : c.name}
              </span>

              {/* Date range */}
              {!draft && dateRange && (
                <span class="shrink-0 text-label text-[color:var(--color-on-surface-variant)]">
                  {dateRange}
                </span>
              )}

              {/* Status badge */}
              <span class="shrink-0 text-label text-[color:var(--color-on-surface-variant)] bg-[color:var(--color-surface-container-low)] rounded px-1.5 py-0.5">
                {STATUS_LABEL[draft ? draft.status : c.status]}
              </span>

              {/* Burndown toggle — always visible */}
              {!draft && (
                <button
                  type="button"
                  onClick={() =>
                    setExpandedBurndown((prev) => {
                      const next = new Set(prev);
                      if (next.has(c.id)) {
                        next.delete(c.id);
                      } else {
                        next.add(c.id);
                      }
                      return next;
                    })
                  }
                  class="shrink-0 inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-label text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-primary)] transition-colors"
                  aria-label="Burndown anzeigen"
                  aria-expanded={expandedBurndown.has(c.id)}
                >
                  {expandedBurndown.has(c.id) ? (
                    <ChevronDown size={14} aria-hidden="true" />
                  ) : (
                    <ChevronRight size={14} aria-hidden="true" />
                  )}
                  Burndown
                </button>
              )}

              {/* Actions (edit role only) */}
              {canEdit && !draft && (
                <div class="flex items-center gap-0.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => startEdit(c)}
                    class="rounded px-1.5 py-0.5 text-label text-[color:var(--color-primary)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-primary)]"
                  >
                    Bearbeiten
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setConfirmDel((prev) => {
                        const next = new Set(prev);
                        next.add(c.id);
                        return next;
                      })
                    }
                    disabled={isSaving}
                    class="rounded p-0.5 text-[color:var(--color-error)] hover:bg-[color:var(--color-error-container)] disabled:opacity-30 focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-error)]"
                    aria-label="Zyklus löschen"
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>

            {/* Inline editor */}
            {draft && canEdit && (
              <div class="border-t border-[color:var(--color-outline-variant)] px-2 pb-2 pt-2 flex flex-col gap-2">
                <input
                  type="text"
                  value={draft.name}
                  maxLength={100}
                  onInput={(e) =>
                    setDraft(c.id, { name: (e.target as HTMLInputElement).value })
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); void commitEdit(c); }
                    if (e.key === "Escape") cancelEdit(c.id);
                  }}
                  placeholder="Name"
                  class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                  aria-label="Name"
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
                <select
                  value={draft.status}
                  onChange={(e) =>
                    setDraft(c.id, {
                      status: (e.target as HTMLSelectElement).value as CycleStatus,
                    })
                  }
                  class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                  aria-label="Status"
                >
                  {CYCLE_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {STATUS_LABEL[s]}
                    </option>
                  ))}
                </select>
                {/* wrap + min-w: a date input will not shrink below ~150px, so on a narrow
                    panel the two fields stack instead of overflowing (#855). */}
                <div class="flex flex-wrap items-center gap-2">
                  <div class="flex-1 min-w-[150px] flex flex-col gap-1">
                    <label class="text-label text-[color:var(--color-on-surface-variant)]">
                      Startdatum
                    </label>
                    <input
                      type="date"
                      value={draft.start_date}
                      onInput={(e) =>
                        setDraft(c.id, { start_date: (e.target as HTMLInputElement).value })
                      }
                      class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                      aria-label="Startdatum"
                    />
                  </div>
                  <div class="flex-1 min-w-[150px] flex flex-col gap-1">
                    <label class="text-label text-[color:var(--color-on-surface-variant)]">
                      Enddatum
                    </label>
                    <input
                      type="date"
                      value={draft.end_date}
                      onInput={(e) =>
                        setDraft(c.id, { end_date: (e.target as HTMLInputElement).value })
                      }
                      class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                      aria-label="Enddatum"
                    />
                  </div>
                </div>
                <div class="flex items-center gap-1.5 justify-end">
                  <button
                    type="button"
                    onClick={() => cancelEdit(c.id)}
                    class="rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
                  >
                    Abbrechen
                  </button>
                  <Button
                    variant="primary"
                    size="xs"
                    type="button"
                    onClick={() => void commitEdit(c)}
                    disabled={isSaving || !draft.name.trim()}
                  >
                    {isSaving ? "Speichere…" : "Speichern"}
                  </Button>
                </div>
              </div>
            )}

            {/* Burndown panel — lazy-rendered, only when expanded */}
            {expandedBurndown.has(c.id) && !draft && (
              <div class="border-t border-[color:var(--color-outline-variant)]">
                <CycleBurndown
                  cycleId={c.id}
                  projectId={projectId}
                  canEdit={canEdit}
                />
              </div>
            )}

            {/* Delete confirm */}
            {isConfirmDel && !draft && canEdit && (
              <div class="border-t border-[color:var(--color-outline-variant)] px-2 py-2 flex flex-col gap-1.5">
                <p class="text-label text-[color:var(--color-on-error-container)] bg-[color:var(--color-error-container)] rounded px-2 py-1.5">
                  Zyklus „{c.name}" wirklich löschen?
                </p>
                <div class="flex items-center gap-1.5 justify-end">
                  <button
                    type="button"
                    onClick={() =>
                      setConfirmDel((prev) => {
                        const next = new Set(prev);
                        next.delete(c.id);
                        return next;
                      })
                    }
                    class="rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
                  >
                    Abbrechen
                  </button>
                  <Button
                    variant="destructive"
                    size="xs"
                    type="button"
                    onClick={() => void handleDelete(c)}
                    disabled={isSaving}
                  >
                    {isSaving ? "Lösche…" : "Löschen"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Add new cycle */}
      {canEdit && (
        <div class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] overflow-hidden">
          {!showNew ? (
            <button
              type="button"
              onClick={() => setShowNew(true)}
              class="w-full inline-flex items-center gap-1.5 px-2 py-1.5 text-xs text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--color-primary)] transition-colors"
            >
              <Plus size={14} aria-hidden="true" />
              Neuen Zyklus anlegen
            </button>
          ) : (
            <div class="px-2 py-2 flex flex-col gap-2">
              <div class="text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
                Neuer Zyklus
              </div>
              <input
                type="text"
                value={newName}
                maxLength={100}
                onInput={(e) =>
                  setNewName((e.target as HTMLInputElement).value)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); void submitNew(); }
                  if (e.key === "Escape") {
                    setShowNew(false);
                    setNewName("");
                  }
                }}
                placeholder="Name des Zyklus"
                class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                aria-label="Name des neuen Zyklus"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
              <select
                value={newStatus}
                onChange={(e) =>
                  setNewStatus(
                    (e.target as HTMLSelectElement).value as CycleStatus
                  )
                }
                class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                aria-label="Status"
              >
                {CYCLE_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABEL[s]}
                  </option>
                ))}
              </select>
              <div class="flex flex-wrap items-center gap-2">
                <div class="flex-1 min-w-[150px] flex flex-col gap-1">
                  <label class="text-label text-[color:var(--color-on-surface-variant)]">
                    Startdatum
                  </label>
                  <input
                    type="date"
                    value={newStartDate}
                    onInput={(e) =>
                      setNewStartDate((e.target as HTMLInputElement).value)
                    }
                    class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                    aria-label="Startdatum"
                  />
                </div>
                <div class="flex-1 min-w-[150px] flex flex-col gap-1">
                  <label class="text-label text-[color:var(--color-on-surface-variant)]">
                    Enddatum
                  </label>
                  <input
                    type="date"
                    value={newEndDate}
                    onInput={(e) =>
                      setNewEndDate((e.target as HTMLInputElement).value)
                    }
                    class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                    aria-label="Enddatum"
                  />
                </div>
              </div>
              <div class="flex items-center gap-1.5 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowNew(false);
                    setNewName("");
                  }}
                  class="rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
                >
                  Abbrechen
                </button>
                <Button
                  variant="primary"
                  size="xs"
                  type="button"
                  onClick={() => void submitNew()}
                  disabled={!newName.trim() || creating}
                >
                  {creating ? "Lege an…" : "Erstellen"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
